// Global packages and components
import { clsx } from 'clsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useLocation,
  useNavigation,
  useNavigationType,
} from '@remix-run/react'
import {
  ProductGrid,
  ListingControlDesktop,
  CategoryBanner,
  BannerSidebar,
  CustomCategories,
  ContentEditor,
  Pagination,
  AttributeFilters,
  Heading,
  FiltersMobile,
  SortMobile,
  PriceRangeFilter,
  SidebarFilter,
  ProductControlMobile,
  Checkbox,
  BlockProductCarousel,
} from '~/components'
import { useTruncated, useProductListing, usePriceFilter } from '~/hooks'
import { shownProductsCount, orderByOptions } from '~/utils'

// Types
import type { PortableTextBlock } from '@portabletext/react'
import type {
  CategoryBannerProps,
  BannerSidebarProps,
  CustomCategoryProps,
  FilterLabel,
  ListingProductsResult,
} from '~/types'

// Remembered per browser rather than held in the URL, so the listing has one
// set of markup for every viewport and no display parameter to crawl
const DISPLAY_STORAGE_KEY = 'plp-display'

interface Props {
  banner?: CategoryBannerProps | null
  bannerSidebar?: BannerSidebarProps | null
  subCategories?: CustomCategoryProps[] | null
  content?: PortableTextBlock[]
  title: string
  filterLabels?: FilterLabel[]
  recentlyViewed?: string[]
  /** Resolved in the route loader so the grid ships in the HTML response */
  listing: ListingProductsResult
}

// Main export
const ProductListing = ({
  banner,
  bannerSidebar,
  subCategories,
  content,
  title,
  filterLabels,
  recentlyViewed,
  listing,
}: Props) => {
  const { products, filters, priceLimit, total, page, pageSize, ok } = listing

  // Offcanvas mobile elements
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false)
  const [sortOpen, setSortOpen] = useState<boolean>(false)
  const [display, setDisplay] = useState<'grid' | 'list'>('list')

  // Refs
  const contentDesktopRef = useRef<HTMLDivElement>(null)
  const contentMobileRef = useRef<HTMLDivElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const previousPage = useRef<number>(page)

  const navigation = useNavigation()
  const location = useLocation()
  const navigationType = useNavigationType()

  // A navigation to this same listing with different params, ie a refinement
  // rather than a page change. The grid stays mounted through it
  const isRefining =
    navigation.state === 'loading' &&
    navigation.location?.pathname === location.pathname &&
    navigation.location?.search !== location.search

  // Controls read the pending URL so a click responds immediately. This is
  // undefined on the server and on the first client render, so both agree
  const uiParams = useMemo(
    () =>
      new URLSearchParams(
        navigation.location?.pathname === location.pathname
          ? navigation.location.search
          : location.search
      ),
    [navigation.location, location.pathname, location.search]
  )

  // Hooks
  const {
    isTruncated: isTruncatedDesktop,
    isShowingMore: isShowingMoreDesktop,
    toggleIsShowingMore: toggleIsShowingMoreDesktop,
  } = useTruncated({
    ref: contentDesktopRef,
  })

  const {
    isTruncated: isTruncatedMobile,
    isShowingMore: isShowingMoreMobile,
    toggleIsShowingMore: toggleIsShowingMoreMobile,
  } = useTruncated({
    ref: contentMobileRef,
  })

  const {
    isFiltered,
    setQueryOrder,
    setQueryOnSale,
    setQueryAttributes,
    setQueryPriceRange,
    clearAll,
  } = useProductListing({ uiParams })

  const { priceValues, setPriceValues } = usePriceFilter({
    priceLimit,
    appliedRange: uiParams.get('price-range'),
    setQueryPriceRange,
  })

  // Read after hydration only, never during render
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(DISPLAY_STORAGE_KEY)

      if (saved === 'grid' || saved === 'list') {
        setDisplay(saved)
      }
    } catch {
      // Private browsing and blocked storage both throw, the default is fine
    }
  }, [])

  // Stop body scroll when offcanvas elements open
  useEffect(() => {
    if (filtersOpen || sortOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }, [filtersOpen, sortOpen])

  // Only a genuine page change scrolls, and only to the results rather than the
  // top of the document. Back and forward are left to scroll restoration
  useEffect(() => {
    if (previousPage.current === page) return

    previousPage.current = page

    if (navigationType === 'POP') return

    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [page, navigationType])

  // Real hrefs so a non rendering crawler can follow pagination into the
  // product layer. Built from uiParams so a click during a pending filter
  // navigation composes rather than resurrecting the pre-click filters
  const getPageHref = useCallback(
    (target: number) => {
      const next = new URLSearchParams(uiParams)

      // Page one drops the param so it cannot duplicate the canonical URL
      if (target <= 1) {
        next.delete('page')
      } else {
        next.set('page', String(target))
      }

      const query = next.toString()

      return query ? `${location.pathname}?${query}` : location.pathname
    },
    [uiParams, location.pathname]
  )

  const changeDisplay = (next: string) => {
    const value = next === 'grid' ? 'grid' : 'list'

    setDisplay(value)

    try {
      window.localStorage.setItem(DISPLAY_STORAGE_KEY, value)
    } catch {
      // Preference simply will not persist, the session still works
    }
  }

  return (
    <>
      <div className="container mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-10">
          <div className="md:col-span-4 lg:col-span-3">
            <div className="mb-6">
              <Heading
                tag="h1"
                size="h4"
              >
                {title}
              </Heading>
              <div className="mb-6 lg:hidden">
                <div
                  ref={contentMobileRef}
                  className={clsx('break-words', {
                    'line-clamp-6': !isShowingMoreMobile,
                    'mb-3': isTruncatedMobile,
                  })}
                >
                  {content && <ContentEditor content={content} />}
                </div>
                {isTruncatedMobile && (
                  <button
                    onClick={toggleIsShowingMoreMobile}
                    className="font-semibold text-teal-green underline"
                  >
                    {isShowingMoreMobile ? 'Read less' : 'Read more'}
                  </button>
                )}
              </div>
              <ProductControlMobile
                setOffcanvasSortOpen={setSortOpen}
                setOffcanvasFiltersOpen={setFiltersOpen}
                isFiltered={isFiltered}
                clearAll={clearAll}
              />
              <div className="hidden lg:block">
                <div
                  ref={contentDesktopRef}
                  className={clsx('break-words', {
                    'line-clamp-6': !isShowingMoreDesktop,
                    'mb-3': isTruncatedDesktop,
                  })}
                >
                  {content && <ContentEditor content={content} />}
                </div>
                {isTruncatedDesktop && (
                  <button
                    onClick={toggleIsShowingMoreDesktop}
                    className="font-semibold text-teal-green underline"
                  >
                    {isShowingMoreDesktop ? 'Read less' : 'Read more'}
                  </button>
                )}
              </div>
            </div>
            <div className="hidden lg:grid">
              <hr className="mb-6 border-neutral-200" />
              {filters && (
                <div className="flex justify-between">
                  <Heading
                    tag="h3"
                    size="h6"
                  >
                    Filters
                  </Heading>
                  <div>
                    {isFiltered && (
                      <button
                        onClick={() => clearAll()}
                        className="font-semibold text-teal-green underline"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>
              )}
              {priceLimit && (
                <SidebarFilter
                  label="Price (£)"
                  defaultOpen={true}
                >
                  <PriceRangeFilter
                    min={priceValues.min}
                    max={priceValues.max}
                    start={priceValues.start}
                    end={priceValues.end}
                    setPriceValues={setPriceValues}
                  />
                </SidebarFilter>
              )}
              <SidebarFilter
                label="Discounts"
                defaultOpen={true}
              >
                <div className="mb-3">
                  <Checkbox
                    id="filter-on-sale"
                    label="Product on sale"
                    checked={uiParams.get('on-sale') == 'true'}
                    onChange={() =>
                      setQueryOnSale(
                        uiParams.get('on-sale') == 'true' ? '' : 'true'
                      )
                    }
                  />
                </div>
              </SidebarFilter>
              {filters && (
                <>
                  <AttributeFilters
                    filters={filters}
                    attributes={uiParams.getAll('attribute')}
                    setQueryAttributes={setQueryAttributes}
                    priceRange={uiParams.get('price-range')}
                    filterLabels={filterLabels}
                  />
                </>
              )}
              {bannerSidebar && (
                <div className="mb-10 hidden lg:block">
                  <BannerSidebar {...bannerSidebar} />
                </div>
              )}
            </div>
          </div>
          <div className="md:col-span-8 lg:col-span-9">
            {subCategories && (
              <div className="mb-6">
                <CustomCategories slides={subCategories} />
              </div>
            )}
            {banner && (
              <div className="mb-10">
                <CategoryBanner {...banner} />
              </div>
            )}
            <p
              role="status"
              aria-live="polite"
              className="sr-only"
            >
              {isRefining ? 'Updating products' : `${total} products found`}
            </p>
            {!ok ? (
              <p>
                Sorry, we could not load products right now. Please try again
                shortly.
              </p>
            ) : products && products?.length > 0 ? (
              <div
                ref={resultsRef}
                aria-busy={isRefining}
                className={clsx('transition-opacity duration-200', {
                  'pointer-events-none opacity-60': isRefining,
                })}
              >
                <ListingControlDesktop
                  total={total}
                  activeDisplay={display}
                  activeOrder={uiParams.get('order')}
                  setDisplay={changeDisplay}
                  setOrderBy={setQueryOrder}
                  orderByOptions={orderByOptions}
                />
                <ProductGrid
                  activeDisplay={display}
                  products={products}
                />
                <div className="mb-8">
                  <div className="mb-4">
                    <Pagination
                      totalCount={total}
                      activePage={page}
                      pageSize={pageSize}
                      getPageHref={getPageHref}
                    />
                  </div>
                  <p className="text-center text-sm text-neutral-500">
                    {shownProductsCount({
                      limit: String(pageSize),
                      page: String(page),
                      total,
                    })}
                  </p>
                </div>
              </div>
            ) : (
              <p>Sorry, there are no products found for your selection</p>
            )}
          </div>
        </div>
      </div>
      {recentlyViewed && recentlyViewed.length > 0 && (
        <div className="mb-16">
          <BlockProductCarousel
            title="Recently viewed"
            productCodes={recentlyViewed}
          />
        </div>
      )}
      <FiltersMobile
        open={filtersOpen}
        filters={filters}
        attributes={uiParams.getAll('attribute')}
        priceValues={priceValues}
        priceLimit={priceLimit}
        filterLabels={filterLabels}
        setOpen={setFiltersOpen}
        setPriceValues={setPriceValues}
        setQueryAttributes={setQueryAttributes}
      />
      <SortMobile
        open={sortOpen}
        activeDisplay={display}
        activeOrder={uiParams.get('order')}
        orderByOptions={orderByOptions}
        setOpen={setSortOpen}
        setDisplay={changeDisplay}
        setOrderBy={setQueryOrder}
      />
    </>
  )
}

export default ProductListing
